import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const fin = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: fin.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: fin.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: fin.primary },
};

const primaryButtonSx = {
  backgroundColor: fin.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: fin.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const formatKsh = (value) =>
  `KSh ${(Number(value) || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const MoneyField = ({ label, value, onChange, disabled = false, helperText }) => (
  <TextField
    label={label}
    type="number"
    value={value}
    onChange={onChange}
    disabled={disabled}
    helperText={helperText}
    variant="outlined"
    fullWidth
    sx={fieldSx}
    InputProps={{
      startAdornment: <InputAdornment position="start">KSh</InputAdornment>,
    }}
  />
);

const EditEarningDialog = ({
  openModal,
  actionType,
  actionLoading,
  selectedEarning,
  handleCloseModal,
  handleConfirmAction,
  editUserType,
  editGrossPay,
  setEditGrossPay,
  editBonus,
  setEditBonus,
  editTax,
  setEditTax,
  editWithholdingTax,
  setEditWithholdingTax,
  editHousingLevy,
  setEditHousingLevy,
  editSha,
  setEditSha,
  editNssf,
  setEditNssf,
  editPaymentDate,
  setEditPaymentDate,
  editPaymentStatus,
  setEditPaymentStatus,
}) => {
  return (
    <Dialog
      open={openModal}
      onClose={actionLoading ? undefined : handleCloseModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", m: { xs: 2, sm: 4 } } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontWeight: 700,
          color: fin.text,
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              actionType === "Delete" ? fin.dangerSoft : fin.primarySoft,
            color: actionType === "Delete" ? fin.danger : fin.primary,
          }}
        >
          {actionType === "Delete" ? (
            <WarningAmberRoundedIcon fontSize="small" />
          ) : (
            <EditOutlinedIcon fontSize="small" />
          )}
        </Box>
        {actionType === "Delete" ? "Delete earning entry" : "Edit earning entry"}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        {actionType === "Delete" ? (
          <Box>
            <Typography sx={{ mb: 2, color: fin.text }}>
              Are you sure you want to delete this earning entry for{" "}
              <strong>{selectedEarning?.name}</strong>? This action cannot be
              undone.
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                backgroundColor: fin.surface,
                border: `1px solid ${fin.border}`,
              }}
            >
              <Typography variant="body2" sx={{ color: fin.muted }}>
                Gross pay
              </Typography>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {formatKsh(selectedEarning?.grossPay)}
              </Typography>
              <Typography variant="body2" sx={{ color: fin.muted }}>
                Payment date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {selectedEarning?.paymentDate}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: fin.surface,
                border: `1px solid ${fin.border}`,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: fin.text }}>
                {selectedEarning?.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: fin.muted, wordBreak: "break-word" }}
              >
                {editUserType} &middot; {selectedEarning?.bankAccount}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2.5,
              }}
            >
              <MoneyField
                label="Gross pay"
                value={editGrossPay}
                onChange={(e) => setEditGrossPay(e.target.value)}
              />
              <MoneyField
                label="Bonus"
                value={editBonus}
                onChange={(e) => setEditBonus(e.target.value)}
              />
              <MoneyField
                label="Tax"
                value={editUserType === "Employee" ? editTax : ""}
                onChange={(e) => {
                  if (editUserType === "Employee") setEditTax(e.target.value);
                }}
                disabled={editUserType !== "Employee"}
              />
              <MoneyField
                label="Withholding tax"
                value={editUserType === "Consultant" ? editWithholdingTax : ""}
                onChange={(e) => {
                  if (editUserType === "Consultant")
                    setEditWithholdingTax(e.target.value);
                }}
                disabled={editUserType !== "Consultant"}
              />
              <MoneyField
                label="Housing levy"
                value={editUserType === "Employee" ? editHousingLevy : ""}
                onChange={(e) => {
                  if (editUserType === "Employee")
                    setEditHousingLevy(e.target.value);
                }}
                disabled={editUserType !== "Employee"}
              />
              <MoneyField
                label="SHA"
                value={editUserType === "Employee" ? editSha : ""}
                onChange={(e) => {
                  if (editUserType === "Employee") setEditSha(e.target.value);
                }}
                disabled={editUserType !== "Employee"}
              />
              <MoneyField
                label="NSSF"
                value={editUserType === "Employee" ? editNssf : ""}
                onChange={(e) => {
                  if (editUserType === "Employee") setEditNssf(e.target.value);
                }}
                disabled={editUserType !== "Employee"}
              />
              <Box sx={{ display: { xs: "none", sm: "block" } }} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Payment Date*"
                  value={editPaymentDate}
                  onChange={setEditPaymentDate}
                  slotProps={{
                    textField: { fullWidth: true, sx: fieldSx },
                  }}
                />
              </LocalizationProvider>
              <TextField
                select
                label="Payment Status*"
                value={editPaymentStatus}
                onChange={(e) => setEditPaymentStatus(e.target.value)}
                fullWidth
                sx={fieldSx}
              >
                <MenuItem value="Yes">Paid</MenuItem>
                <MenuItem value="No">Not paid</MenuItem>
              </TextField>
            </Box>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleCloseModal}
          disabled={actionLoading}
          sx={{ color: fin.muted, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmAction}
          disabled={actionLoading}
          startIcon={
            actionLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : null
          }
          sx={{
            ...primaryButtonSx,
            py: 1,
            backgroundColor: actionType === "Edit" ? fin.primary : fin.danger,
            "&:hover": {
              backgroundColor:
                actionType === "Edit" ? fin.primaryDark : "#b71c1c",
            },
            "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
          }}
        >
          {actionType === "Edit" ? "Save changes" : "Delete entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEarningDialog;